import { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/Seo";
import { CONTACT_INFO } from "@/data/siteData";
import { sendAppointmentEmail } from "@/lib/emailjsClient";
import { trackFormSubmit } from "@/lib/gtm";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const AppointmentPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    setIsSubmitting(true);
    try {
      await sendAppointmentEmail(formData);
      trackFormSubmit("appointment");
      setSubmitted(true);
    } catch (error) {
      console.error("Appointment form error:", error);
      toast.error("Възникна грешка при изпращането.", {
        description: "Моля, опитайте отново или ни пишете директно на " + CONTACT_INFO.email,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout showContactStrip={false}>
        <Seo title="Заявката е изпратена" path="/zapazi-chas" noindex />
        <section className="min-h-[70vh] flex items-center justify-center py-32">
          <div className="max-w-lg mx-auto px-4 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-[#2ED573]/10 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#2ED573]" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
              Благодарим ви!
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Вашата заявка е изпратена успешно. Ще се свържем с вас възможно
              най-скоро за потвърждение на удобен час за консултация.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button className="btn-primary">
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Към началото
                </Button>
              </Link>
              <Link to="/uslugi">
                <Button variant="outline" className="btn-outline">
                  Разгледайте услугите
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout showContactStrip={false}>
      <Seo path="/zapazi-chas" />
      {/* Page Header */}
      <section className="bg-gradient-to-r from-[#2E4A91] to-[#243b75] pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <div className="w-16 h-16 mx-auto mb-6 bg-white/10 rounded-full flex items-center justify-center">
              <Calendar className="w-8 h-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mt-3">
              Запазете час
            </h1>
            <p className="text-lg text-white/80 mt-4 max-w-2xl mx-auto">
              Попълнете формата и ние ще се свържем с вас за потвърждение на
              удобен час за консултация
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-10">
            <form
              onSubmit={handleSubmit}
              className="space-y-6"
              data-testid="appointment-form"
            >
              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label
                    htmlFor="firstName"
                    className="text-slate-700 font-medium"
                  >
                    Име *
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    className="mt-2 bg-white"
                    placeholder="Иван"
                    required
                    data-testid="appointment-firstname-input"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="lastName"
                    className="text-slate-700 font-medium"
                  >
                    Фамилия *
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    className="mt-2 bg-white"
                    placeholder="Иванов"
                    required
                    data-testid="appointment-lastname-input"
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label
                    htmlFor="email"
                    className="text-slate-700 font-medium"
                  >
                    Имейл *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="mt-2 bg-white"
                    placeholder="email@example.com"
                    required
                    data-testid="appointment-email-input"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="phone"
                    className="text-slate-700 font-medium"
                  >
                    Телефон *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className="mt-2 bg-white"
                    placeholder="+359 888 123 456"
                    required
                    data-testid="appointment-phone-input"
                  />
                </div>
              </div>

              {/* Company */}
              <div>
                <Label
                  htmlFor="company"
                  className="text-slate-700 font-medium"
                >
                  Компания
                </Label>
                <Input
                  id="company"
                  type="text"
                  value={formData.company}
                  onChange={(e) =>
                    setFormData({ ...formData, company: e.target.value })
                  }
                  className="mt-2 bg-white"
                  placeholder="Име на компанията (ако е приложимо)"
                  data-testid="appointment-company-input"
                />
              </div>

              {/* Service Interest */}
              <div>
                <Label
                  htmlFor="service"
                  className="text-slate-700 font-medium"
                >
                  Каква услуга ви интересува?
                </Label>
                <select
                  id="service"
                  value={formData.service}
                  onChange={(e) =>
                    setFormData({ ...formData, service: e.target.value })
                  }
                  className="mt-2 w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2E4A91] focus:border-transparent"
                  data-testid="appointment-service-select"
                >
                  <option value="">Изберете услуга</option>
                  <option value="call-center">Кол Център услуги</option>
                  <option value="chat-channels">Управление на чат канали</option>
                  <option value="sales-teams">Търговски екипи</option>
                  <option value="full-package">Пълен пакет услуги</option>
                  <option value="other">Друго</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <Label
                  htmlFor="message"
                  className="text-slate-700 font-medium"
                >
                  Съобщение
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="mt-2 bg-white min-h-[150px]"
                  placeholder="Опишете накратко вашите нужди или въпроси..."
                  data-testid="appointment-message-input"
                />
              </div>

              <ConsentCheckbox id="appointment-consent" checked={consent} onChange={setConsent} />

              {/* Submit */}
              <div className="pt-4">
                <Button
                  type="submit"
                  className="btn-primary w-full text-lg py-6"
                  data-testid="appointment-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Изпращане...
                    </>
                  ) : (
                    <>
                      <Calendar className="mr-2 w-5 h-5" />
                      Изпратете заявка
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Info */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex items-start gap-3 text-sm text-slate-500">
                <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p>
                  След изпращане на заявката, наш представител ще се свърже с вас
                  в рамките на 24 часа за потвърждение на удобен час за
                  консултация.
                </p>
              </div>
            </div>
          </div>

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link
              to="/"
              className="text-[#2E4A91] font-medium hover:text-[#2ED573] transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Обратно към началото
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AppointmentPage;
